import React from 'react';
import { X, Info } from 'lucide-react';
import { useOfferings } from './OfferingsProvider';

/**
 * PreviewBanner — slim, dismissible disclaimer shown at the top of every session.
 *
 * Intentionally NOT persisted (no localStorage) — it reappears every fresh load/session
 * so every stakeholder/tester sees it, but a single tester can dismiss it while they work.
 *
 * Development only. It shipped to production until 2026-09-17, telling real members they
 * were looking at a design preview. Set REACT_APP_SHOW_PREVIEW_BANNER=true to force it on
 * in a production build when demoing.
 */
const SHOW_BANNER = process.env.NODE_ENV !== 'production'
  || process.env.REACT_APP_SHOW_PREVIEW_BANNER === 'true';

export default function PreviewBanner() {
  const [dismissed, setDismissed] = React.useState(false);
  const { source, role, setRole, roles, loading } = useOfferings();

  if (!SHOW_BANNER || dismissed) return null;

  // A deployed build has no staff token, so it is left with Member alone.
  const canSwitchRole = roles.length > 1;
  const sourceLabel = loading
    ? 'checking Directus…'
    : source === 'directus'
      ? 'live from Directus'
      : 'mocked (Directus unreachable)';

  return (
    <div
      data-testid="preview-banner"
      className="bg-amber-50 border-b border-[#D4A843]/40 px-4 py-2 flex items-start gap-2 text-amber-900"
    >
      <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#D4A843]" />
      <p className="flex-1 text-xs leading-snug">
        <span className="font-semibold">Design preview</span>
        {' — '}
        cards are {sourceLabel}.{canSwitchRole ? ' Switch role to prove hide/show.' : ''}
      </p>
      {canSwitchRole && (
        <label className="flex items-center gap-1 text-[11px] font-semibold shrink-0">
          <span className="sr-only">Viewing as</span>
          <select
            data-testid="cms-role-switcher"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="h-6 rounded-md border border-[#D4A843]/50 bg-white px-1.5 text-[11px] font-semibold text-amber-950"
          >
            {roles.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
      )}
      <button
        type="button"
        data-testid="preview-banner-dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="flex-shrink-0 -mt-0.5 h-6 w-6 rounded-full hover:bg-amber-100 flex items-center justify-center"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
